import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  isPushSupported,
  getPermissionState,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../services/pushNotifications';
import './NotificationSettings.css';

const NotificationSettings = ({ isPremium }) => {
  const { t } = useTranslation();
  const [pushSupported] = useState(isPushSupported());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [permissionState, setPermissionState] = useState(getPermissionState());
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadState = useCallback(async () => {
    try {
      const [subscribed, preferences] = await Promise.all([
        isSubscribedToPush(),
        getNotificationPreferences(),
      ]);
      setPushSubscribed(subscribed);
      setPrefs(preferences);
    } catch (err) {
      console.error('Failed to load notification settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const handlePushToggle = async () => {
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        toast.success(t('notifications.pushDisabled'));
      } else {
        await subscribeToPush();
        setPushSubscribed(true);
        setPermissionState(getPermissionState());
        toast.success(t('notifications.pushEnabled'));
      }
    } catch (err) {
      if (Notification.permission === 'denied') {
        toast.error(t('notifications.permissionDenied'));
      } else {
        toast.error(t('notifications.pushError'));
      }
      setPermissionState(getPermissionState());
    }
  };

  const handlePrefChange = async (field, value) => {
    setSaving(true);
    try {
      const updated = await updateNotificationPreferences({ [field]: value });
      setPrefs(updated);
    } catch (err) {
      toast.error(t('notifications.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t('common.loading')}</p>;
  }

  if (!prefs) {
    return null;
  }

  return (
    <div className="notification-settings">
      {/* Push toggle */}
      {pushSupported && (
        <div className="notification-setting">
          <div className="notification-setting__row">
            <div>
              <span className="notification-setting__label">
                {t('notifications.pushNotifications')}
              </span>
              {permissionState === 'denied' && (
                <p className="notification-setting__hint notification-setting__hint--warn">
                  {t('notifications.browserBlocked')}
                </p>
              )}
            </div>
            <button
              type="button"
              className={`toggle-btn ${pushSubscribed ? 'is-on' : ''}`}
              onClick={handlePushToggle}
              disabled={permissionState === 'denied'}
              aria-pressed={pushSubscribed}
            >
              <span className="toggle-btn__knob" />
            </button>
          </div>
        </div>
      )}

      {/* Email toggle */}
      <div className="notification-setting">
        <div className="notification-setting__row">
          <span className="notification-setting__label">
            {t('notifications.emailNotifications')}
          </span>
          <button
            type="button"
            className={`toggle-btn ${prefs.emailEnabled ? 'is-on' : ''}`}
            onClick={() => handlePrefChange('emailEnabled', !prefs.emailEnabled)}
            disabled={saving}
            aria-pressed={prefs.emailEnabled}
          >
            <span className="toggle-btn__knob" />
          </button>
        </div>
      </div>

      {/* Reminder timing */}
      <h4 className="notification-settings__section-title">
        {t('notifications.reminderTiming')}
      </h4>

      <label className="notification-checkbox">
        <input
          type="checkbox"
          checked={prefs.reminderEveningBefore}
          onChange={() =>
            handlePrefChange('reminderEveningBefore', !prefs.reminderEveningBefore)
          }
          disabled={saving}
        />
        <span>{t('notifications.eveningBefore')}</span>
      </label>

      <label className="notification-checkbox">
        <input
          type="checkbox"
          checked={prefs.reminderMorningOf}
          onChange={() =>
            handlePrefChange('reminderMorningOf', !prefs.reminderMorningOf)
          }
          disabled={saving}
        />
        <span>{t('notifications.morningOf')}</span>
      </label>

      <label className="notification-checkbox">
        <input
          type="checkbox"
          checked={prefs.reminderOneHour}
          onChange={() =>
            handlePrefChange('reminderOneHour', !prefs.reminderOneHour)
          }
          disabled={saving || !isPremium}
        />
        <span>
          {t('notifications.oneHourBefore')}
          {!isPremium && (
            <span className="notification-premium-badge">Premium</span>
          )}
        </span>
      </label>

      {/* Event types */}
      <h4 className="notification-settings__section-title">
        {t('notifications.eventTypes')}
      </h4>

      {[
        { field: 'notifyDriverAssigned', label: t('notifications.driverAssigned') },
        { field: 'notifyDriverUnassigned', label: t('notifications.driverUnassigned') },
        { field: 'notifyRideCancelled', label: t('notifications.rideCancelled') },
        { field: 'notifyChildPickedUp', label: t('notifications.childPickedUp') },
        { field: 'notifyChildDroppedOff', label: t('notifications.childDroppedOff') },
      ].map(({ field, label }) => (
        <label key={field} className="notification-checkbox">
          <input
            type="checkbox"
            checked={prefs[field]}
            onChange={() => handlePrefChange(field, !prefs[field])}
            disabled={saving}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
};

export default NotificationSettings;
